/**
 * @fileoverview Loader for a single WINBIT32 vault co-signing share (`.wult`).
 *
 * This is Phase 1 of in-browser MPC co-signing for Secresea: load ONE Orchard
 * FROST share here, then co-sign a shielded transaction with a WINBIT32
 * cosigner ("cosign.exe") on another device over the WB32COSIGN relay that the
 * NFPT API already hosts at `/api/cosign`.
 *
 * The on-disk `.wult` format is owned by WINBIT32's `vaultWrapperFormat.js`.
 * This module is a faithful, byte-compatible port of the *load* path so a
 * share exported from WINBIT32 opens here unchanged:
 *
 *   {
 *     "type": "winbit32-vault-v2",
 *     "version": 2,
 *     "vaultData": "<base64 Vultisig SDK protobuf export>",
 *     "encrypted": <bool>,
 *     "crossChainIdentity"?: { "hexChainCode", "publicKeyEcdsa" },
 *     "frost": { "orchard"?: <JSON | "salt.iv.ct">, "sapling"?: ... }
 *   }
 *
 * When `encrypted` is true each FROST payload is PBKDF2-SHA-256 (600k iters) →
 * AES-GCM-256, packed as `base64(salt).base64(iv).base64(ciphertext)`. These
 * parameters MUST match WINBIT32 exactly, hence the constants below are not
 * configurable.
 *
 * Scope note: native Vultisig `.vult` files (Orchard via the SDK's
 * `chainKeyShares`) need the Vultisig SDK + frozto WASM to unpack and are
 * handled in a later phase; this loader covers the `.wult` wrapper only.
 */

// Must match WINBIT32 vaultWrapperFormat.js — do not change.
export const VAULT_V2_TYPE = 'winbit32-vault-v2';
const FROST_KDF_ITER = 600_000;

/** Opaque Orchard FROST key bundle (shape owned by the orchard FROST WASM). */
export type OrchardFrostBundle = Record<string, unknown>;

export interface CrossChainIdentity {
	hexChainCode: string;
	publicKeyEcdsa: string;
}

export interface LoadedVaultShare {
	/** base64 Vultisig SDK protobuf export (transparent/EVM shares). */
	sdkVaultString: string;
	/** Decrypted Orchard FROST bundle, or null if the file carries none. */
	orchardFrost: OrchardFrostBundle | null;
	/** Opaque base64 Sapling FROST bundle, or null. */
	saplingBundleBase64: string | null;
	/** Chain code + ECDSA pubkey for deterministic Zcash/UA derivation. */
	crossChainIdentity: CrossChainIdentity | null;
	/** Whether the file required (and was opened with) a password. */
	encrypted: boolean;
}

// ─── base64 / binary helpers (match WINBIT32 exactly) ───────────────

const b64Enc = (buf: ArrayBuffer | Uint8Array): string => {
	const bytes = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
	let bin = '';
	for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
	return btoa(bin);
};

const b64Dec = (str: string): Uint8Array => {
	const bin = atob(str);
	const a = new Uint8Array(bin.length);
	for (let i = 0; i < bin.length; i++) a[i] = bin.charCodeAt(i);
	return a;
};

const getSubtle = (): SubtleCrypto => {
	const c = (typeof window !== 'undefined' ? window.crypto : (globalThis as any).crypto) as Crypto | undefined;
	if (!c || !c.subtle) {
		throw new Error('Web Crypto API unavailable — a secure (HTTPS) context is required to open an encrypted share.');
	}
	return c.subtle;
};

const getRandomBytes = (n: number): Uint8Array => {
	const c = (typeof window !== 'undefined' ? window.crypto : (globalThis as any).crypto) as Crypto;
	return c.getRandomValues(new Uint8Array(n));
};

// ─── PBKDF2 + AES-GCM payload encryption (match WINBIT32 exactly) ────

const frostEncrypt = async (jsonStr: string, password: string): Promise<string> => {
	const subtle = getSubtle();
	const enc = new TextEncoder();
	const salt = getRandomBytes(16);
	const iv = getRandomBytes(12);
	const km = await subtle.importKey('raw', enc.encode(password), 'PBKDF2', false, ['deriveKey']);
	const key = await subtle.deriveKey(
		{ name: 'PBKDF2', salt, iterations: FROST_KDF_ITER, hash: 'SHA-256' },
		km,
		{ name: 'AES-GCM', length: 256 },
		false,
		['encrypt']
	);
	const ct = await subtle.encrypt({ name: 'AES-GCM', iv }, key, enc.encode(jsonStr));
	return `${b64Enc(salt)}.${b64Enc(iv)}.${b64Enc(ct)}`;
};

const frostDecrypt = async (encoded: string, password: string): Promise<string> => {
	const [sB, iB, cB] = encoded.split('.');
	if (!sB || !iB || !cB) throw new Error('Malformed encrypted share blob.');
	const subtle = getSubtle();
	const km = await subtle.importKey('raw', new TextEncoder().encode(password), 'PBKDF2', false, ['deriveKey']);
	const key = await subtle.deriveKey(
		{ name: 'PBKDF2', salt: b64Dec(sB), iterations: FROST_KDF_ITER, hash: 'SHA-256' },
		km,
		{ name: 'AES-GCM', length: 256 },
		false,
		['decrypt']
	);
	let plain: ArrayBuffer;
	try {
		plain = await subtle.decrypt({ name: 'AES-GCM', iv: b64Dec(iB) }, key, b64Dec(cB));
	} catch {
		throw new Error('Incorrect password (could not decrypt the share).');
	}
	return new TextDecoder().decode(plain);
};

// ─── Public API ─────────────────────────────────────────────────────

/** Cheap structural sniff for a `.wult` v2 wrapper (never throws). */
export const isWrappedVault = (content: unknown): boolean => {
	if (typeof content !== 'string' || content[0] !== '{') return false;
	try {
		return (JSON.parse(content) as { type?: string })?.type === VAULT_V2_TYPE;
	} catch {
		return false;
	}
};

/**
 * Load a `.wult` share, decrypting its FROST payloads when a password is set.
 * Throws if the content isn't a v2 wrapper or the password is wrong.
 *
 * Individual payload decryption failures (e.g. a corrupt Sapling blob) leave
 * that field null rather than aborting the whole load — matching WINBIT32 — so
 * a usable Orchard share still opens.
 */
export const unwrapVaultShare = async (content: string, password?: string): Promise<LoadedVaultShare> => {
	let obj: any;
	try {
		obj = JSON.parse(content);
	} catch {
		throw new Error('This file is not a valid Winbit32 vault share.');
	}
	if (obj?.type !== VAULT_V2_TYPE) {
		throw new Error('Not a Winbit32 co-signing share (.wult).');
	}

	const encrypted = !!obj.encrypted;
	if (encrypted && !password) {
		throw new Error('This share is password-protected. Enter its password to open it.');
	}

	const result: LoadedVaultShare = {
		sdkVaultString: obj.vaultData,
		orchardFrost: null,
		saplingBundleBase64: null,
		crossChainIdentity: null,
		encrypted,
	};

	if (obj.crossChainIdentity?.hexChainCode && obj.crossChainIdentity?.publicKeyEcdsa) {
		result.crossChainIdentity = {
			hexChainCode: obj.crossChainIdentity.hexChainCode,
			publicKeyEcdsa: obj.crossChainIdentity.publicKeyEcdsa,
		};
	}

	if (obj.frost?.orchard) {
		const raw = (encrypted && password) ? await frostDecrypt(obj.frost.orchard, password) : obj.frost.orchard;
		result.orchardFrost = typeof raw === 'string' ? JSON.parse(raw) : raw;
	}

	if (obj.frost?.sapling) {
		try {
			const raw = (encrypted && password) ? await frostDecrypt(obj.frost.sapling, password) : obj.frost.sapling;
			result.saplingBundleBase64 = typeof raw === 'string' ? raw : null;
		} catch (err) {
			console.warn('Sapling share extraction failed:', err instanceof Error ? err.message : err);
		}
	}

	return result;
};

// Exposed for tests only (build a fixture without a real WINBIT32 export).
export const __test_internals = { frostEncrypt, frostDecrypt };
